import axios, { AxiosError } from 'axios';
import { invoke } from '@tauri-apps/api/core';
import { isTauri } from './tauri';
import { useCertificateStore } from '../store/certificateStore';

const urlBuilder = axios.create();

/** `send_http_request`'s total timeout when no override is sent (lib.rs). */
const RUST_DEFAULT_TIMEOUT_MS = 30_000;

let browserAdapter;

function getBrowserAdapter() {
  if (!browserAdapter) {
    browserAdapter = axios.getAdapter(['xhr', 'http', 'fetch']);
  }
  return browserAdapter;
}

function headersToPlainObject(headers) {
  const out = {};
  if (!headers) return out;
  if (typeof headers.forEach === 'function') {
    headers.forEach((value, key) => {
      if (value === undefined || value === null) return;
      const v = Array.isArray(value) ? value.join(', ') : String(value);
      if (v !== '') out[key] = v;
    });
  } else if (typeof headers === 'object') {
    Object.keys(headers).forEach((key) => {
      const value = headers[key];
      if (value !== undefined && value !== null) out[key] = String(value);
    });
  }
  return out;
}

function serializeBody(data) {
  if (data == null || data === '') return null;
  if (typeof data === 'string') return data;
  if (typeof data === 'object' && !(data instanceof ArrayBuffer) && !(data instanceof Uint8Array)) {
    return JSON.stringify(data);
  }
  return String(data);
}

function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  // Chunk to stay well under the argument-count limit of String.fromCharCode.
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

/**
 * Convert a browser FormData into the shape the Rust `send_http_request` command
 * expects: text parts pass through, while File/Blob values are read into base64
 * `file_bytes` (so in-memory blobs with no disk path — e.g. a pasted image —
 * upload correctly). Without this the Tauri adapter would JSON.stringify the
 * FormData to "{}" and drop every file.
 */
async function formDataToMultipart(form) {
  const parts = [];
  const entries = [];
  form.forEach((value, key) => entries.push([key, value]));
  for (const [name, value] of entries) {
    if (typeof Blob !== 'undefined' && value instanceof Blob) {
      const buf = await value.arrayBuffer();
      parts.push({
        name,
        file_bytes: arrayBufferToBase64(buf),
        file_name: (typeof File !== 'undefined' && value instanceof File && value.name) || 'file',
      });
    } else {
      parts.push({ name, text: String(value) });
    }
  }
  return parts;
}

function pickContentType(headersObj) {
  const keys = Object.keys(headersObj || {});
  for (const k of keys) {
    if (k.toLowerCase() === 'content-type') return headersObj[k];
  }
  return '';
}

function parseResponseData(rawData, responseHeaders) {
  if (rawData == null || rawData === '') return '';
  const ct = pickContentType(responseHeaders).toLowerCase();
  if (ct.includes('application/json') || ct.includes('+json')) {
    try {
      return JSON.parse(rawData);
    } catch {
      return rawData;
    }
  }
  return rawData;
}

function settleErrorCode(status) {
  const idx = Math.floor(status / 100) - 4;
  if (idx === 0 || idx === 1) {
    return [AxiosError.ERR_BAD_REQUEST, AxiosError.ERR_BAD_RESPONSE][idx];
  }
  return AxiosError.ERR_BAD_RESPONSE;
}

/**
 * Axios adapter: in Tauri, HTTP runs in Rust (reqwest) to avoid browser CORS.
 * Otherwise delegates to the default XHR/fetch adapter.
 */
export function tauriAxiosAdapter(config) {
  if (!isTauri()) {
    return getBrowserAdapter()(config);
  }
  return sendViaRust(config);
}

async function sendViaRust(config) {
  const method = (config.method || 'get').toUpperCase();
  const url = urlBuilder.getUri(config);
  const headers = headersToPlainObject(config.headers);

  let body = null;
  let multipart = null;
  if (!['GET', 'HEAD'].includes(method) && config.data != null && config.data !== '') {
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      multipart = await formDataToMultipart(config.data);
      // Let reqwest set multipart/form-data with the correct boundary; a
      // pre-set content-type without a boundary would break server parsing.
      for (const k of Object.keys(headers)) {
        if (k.toLowerCase() === 'content-type') delete headers[k];
      }
    } else {
      body = serializeBody(config.data);
    }
  }

  const { clientCerts, caCerts } = useCertificateStore.getState();

  // Build TLS config from the certificate store.
  // Passphrases are kept in session state on each cert object (never persisted).
  const tls = {
    client_certs: clientCerts.map((c) => ({
      domain: c.domain,
      cert_type: c.type,
      cert_path: c.certPath,
      key_path: c.keyPath || null,
      passphrase: c.passphrase || null,
    })),
    ca_certs: caCerts.map((c) => c.path),
    // Always verified. This adapter carries Apiboo's own traffic (the backend
    // with the auth token, the idp, GitHub); Settings → Requests → SSL
    // verification is about the user's requests, never these. Rust enforces
    // it too (request_preferences::enforce_ssl_policy).
    verify_ssl: true,
  };

  // Rust applies a 30 s total timeout unless told otherwise, and axios'
  // `timeout` never reached it — so a caller that asked for MORE (the cloud
  // import's 60 s, PR #305 review) silently got 30 s in the desktop app. Only a
  // longer budget is forwarded: the client-wide 10 s default stays the 30 s
  // every Tauri caller has always had.
  const timeoutSecs = Number(config.timeout) > RUST_DEFAULT_TIMEOUT_MS
    ? Math.ceil(Number(config.timeout) / 1000)
    : undefined;

  return invoke('send_http_request', {
    request: {
      method,
      url,
      headers,
      body,
      multipart,
      tls,
      ...(timeoutSecs ? { timeout_secs: timeoutSecs } : {}),
    },
  })
    .then((res) => {
      const data = parseResponseData(res.data, res.headers || {});
      const response = {
        data,
        status: res.status,
        statusText: res.status_text || '',
        headers: res.headers || {},
        config,
        request: {},
      };

      const validateStatus = config.validateStatus;
      if (!response.status || !validateStatus || validateStatus(response.status)) {
        return response;
      }

      return Promise.reject(
        new AxiosError(
          'Request failed with status code ' + response.status,
          settleErrorCode(response.status),
          config,
          null,
          response
        )
      );
    })
    .catch((err) => {
      if (err?.isAxiosError) {
        return Promise.reject(err);
      }
      const msg = typeof err === 'string' ? err : err?.message || String(err);
      return Promise.reject(
        AxiosError.from(new Error(msg), AxiosError.ERR_NETWORK, config, null, null)
      );
    });
}
